import { BadgeCheck, ShieldCheck, Star, Award } from "lucide-react";
import { cn } from "@/lib/utils";

import stampAsset from "@/assets/brand/stamp.png.asset.json";

/**
 * Peopels trust badge system. Built on the approved circular stamp:
 * a navy ring with "TRUSTED PROFESSIONALS / PEOPELS" wrapping the
 * full-colour mark. Each variant swaps the inner caption + accent icon
 * so the visual language is unmistakably one family.
 *
 * The base artwork (`stamp.png`) is the canonical "Trusted Professional"
 * stamp. For the other badges we layer a small icon + label chip on top
 * of a neutral ring so the brand stamp remains the hero, not visually
 * outranked by variants.
 */

export type TrustBadgeVariant =
  | "trusted-professional"
  | "verified-professional"
  | "id-verified"
  | "top-rated";

const META: Record<
  TrustBadgeVariant,
  {
    label: string;
    sub: string;
    Icon: typeof BadgeCheck;
    tone: "navy" | "emerald" | "indigo" | "amber";
  }
> = {
  "trusted-professional": {
    label: "Trusted Professional",
    sub: "Peopels approved",
    Icon: Award,
    tone: "navy",
  },
  "verified-professional": {
    label: "Verified Professional",
    sub: "Skills checked",
    Icon: BadgeCheck,
    tone: "emerald",
  },
  "id-verified": {
    label: "ID Verified",
    sub: "Government ID confirmed",
    Icon: ShieldCheck,
    tone: "indigo",
  },
  "top-rated": {
    label: "Top Rated",
    sub: "4.8+ over 20 jobs",
    Icon: Star,
    tone: "amber",
  },
};

const TONE_CLASSES: Record<
  "navy" | "emerald" | "indigo" | "amber",
  { ring: string; iconBg: string; icon: string }
> = {
  navy: {
    ring: "border-[#0B132B]/15",
    iconBg: "bg-[#0B132B]",
    icon: "text-white",
  },
  emerald: {
    ring: "border-emerald-600/20",
    iconBg: "bg-emerald-600",
    icon: "text-white",
  },
  indigo: {
    ring: "border-[#2563EB]/20",
    iconBg: "bg-[#2563EB]",
    icon: "text-white",
  },
  amber: {
    ring: "border-amber-500/30",
    iconBg: "bg-amber-500",
    icon: "text-[#0B132B]",
  },
};

type Props = {
  variant: TrustBadgeVariant;
  /** Visual style. Default `chip` is compact and used inline. `stamp` is the full circular mark. */
  display?: "chip" | "stamp";
  className?: string;
  /** Override label (e.g. localised). */
  label?: string;
};

export function TrustBadge({ variant, display = "chip", className, label }: Props) {
  const meta = META[variant];
  const tone = TONE_CLASSES[meta.tone];
  const Icon = meta.Icon;
  const text = label ?? meta.label;

  if (display === "stamp") {
    return (
      <div
        className={cn("relative inline-flex flex-col items-center", className)}
        role="img"
        aria-label={`Peopels ${text} badge`}
      >
        <img
          src={stampAsset.url}
          width={120}
          height={120}
          alt=""
          aria-hidden="true"
          decoding="async"
          loading="lazy"
          draggable={false}
          className="h-[120px] w-[120px]"
        />
        {variant !== "trusted-professional" && (
          <div
            className={cn(
              "absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/3 rounded-full border bg-card px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider shadow-sm",
              tone.ring,
            )}
          >
            <span className="inline-flex items-center gap-1">
              <Icon className={cn("h-3 w-3", tone.icon === "text-white" ? "text-[#0B132B]" : tone.icon)} aria-hidden="true" />
              {text}
            </span>
          </div>
        )}
      </div>
    );
  }

  // Chip variant — compact inline pill for cards / profiles.
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground shadow-sm",
        tone.ring,
        className,
      )}
      title={meta.sub}
    >
      <span className={cn("grid h-4 w-4 place-items-center rounded-full", tone.iconBg)}>
        <Icon className={cn("h-2.5 w-2.5", tone.icon)} aria-hidden="true" />
      </span>
      {text}
    </span>
  );
}
